import { useEffect, useRef, useState } from "react";
import { ZoomIn, ZoomOut, Maximize, RotateCw, Pause } from "lucide-react";

const PANNELLUM_JS =
    "https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.js";
const PANNELLUM_CSS =
    "https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.css";

let pannellumPromise = null;

export function loadPannellum() {
    if (pannellumPromise) return pannellumPromise;
    pannellumPromise = new Promise((resolve, reject) => {
        if (window.pannellum) return resolve();
        if (!document.querySelector(`link[href="${PANNELLUM_CSS}"]`)) {
            const link = document.createElement("link");
            link.rel = "stylesheet";
            link.href = PANNELLUM_CSS;
            document.head.appendChild(link);
        }
        const script = document.createElement("script");
        script.src = PANNELLUM_JS;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("pannellum failed to load"));
        document.head.appendChild(script);
    });
    return pannellumPromise;
}

export default function PanoViewer({
    image,
    hotspots = [],
    autoRotate = -1.6,
    hfov = 105,
    pitch = 0,
    yaw = 0,
    className = "",
    testIdPrefix = "pano",
    heightClass = "h-[62vh] min-h-[420px]",
}) {
    const containerRef = useRef(null);
    const viewerRef = useRef(null);
    const [ready, setReady] = useState(false);
    const [failed, setFailed] = useState(false);
    const [rotating, setRotating] = useState(autoRotate !== 0);

    useEffect(() => {
        let destroyed = false;
        loadPannellum()
            .then(() => {
                if (destroyed || !containerRef.current) return;
                try {
                    viewerRef.current = window.pannellum.viewer(
                        containerRef.current,
                        {
                            type: "equirectangular",
                            panorama: image,
                            autoLoad: true,
                            autoRotate,
                            autoRotateInactivityDelay: 1800,
                            pitch,
                            yaw,
                            hfov,
                            compass: false,
                            showControls: false,
                            showZoomCtrl: false,
                            showFullscreenCtrl: false,
                            mouseZoom: true,
                            draggable: true,
                            hotSpots: hotspots.map((h) => ({
                                pitch: h.pitch,
                                yaw: h.yaw,
                                type: "info",
                                text: h.text,
                                cssClass: "custom-hotspot",
                            })),
                        }
                    );
                    viewerRef.current.on("load", () => {
                        if (!destroyed) setReady(true);
                    });
                    viewerRef.current.on("error", () => {
                        if (!destroyed) setFailed(true);
                    });
                } catch (e) {
                    setFailed(true);
                }
            })
            .catch(() => setFailed(true));
        return () => {
            destroyed = true;
            try {
                viewerRef.current && viewerRef.current.destroy();
            } catch (e) {
                /* noop */
            }
            viewerRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [image]);

    const zoom = (dir) => {
        const v = viewerRef.current;
        if (!v) return;
        const next = v.getHfov() + dir * 12;
        v.setHfov(Math.min(120, Math.max(45, next)));
    };

    const toggleRotate = () => {
        const v = viewerRef.current;
        if (!v) return;
        if (rotating) {
            v.setAutoRotate(0);
            v.stopAutoRotate?.();
        } else {
            v.setAutoRotate(autoRotate || -1.6);
            v.startAutoRotate?.(autoRotate || -1.6);
        }
        setRotating(!rotating);
    };

    const fullscreen = () => {
        viewerRef.current && viewerRef.current.toggleFullscreen();
    };

    return (
        <div
            className={`group relative overflow-hidden rounded-2xl border border-[#00C6FF]/25 shadow-[0_0_50px_rgba(0,198,255,0.12)] ${className}`}
            data-testid={`${testIdPrefix}-viewer`}
        >
            <div ref={containerRef} className={`w-full ${heightClass} bg-[#0A1F44]`} />

            {!ready && !failed && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-[#050B14]/90">
                    <div className="h-12 w-12 animate-spin rounded-full border-2 border-[#00C6FF]/20 border-t-[#00C6FF]" />
                    <p className="font-mono-accent text-xs tracking-[0.3em] text-[#00C6FF]">
                        LOADING 360° SPACE
                    </p>
                </div>
            )}

            {failed && (
                <div className="absolute inset-0">
                    <img
                        src={image}
                        alt="360° preview"
                        className="h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050B14] via-transparent to-transparent" />
                </div>
            )}

            <div className="pointer-events-none absolute left-4 top-4 z-10 flex items-center gap-2 rounded-full border border-[#00C6FF]/30 bg-[#050B14]/70 px-4 py-2 backdrop-blur-md">
                <span className="h-2 w-2 animate-pulse rounded-full bg-[#00C6FF]" />
                <span className="font-mono-accent text-[10px] tracking-[0.3em] text-[#00C6FF]">
                    LIVE 360°
                </span>
            </div>

            <div className="pointer-events-none absolute bottom-4 left-1/2 z-10 -translate-x-1/2 rounded-full border border-white/10 bg-[#050B14]/60 px-5 py-2 text-xs tracking-widest text-slate-300 backdrop-blur-md transition-opacity duration-500 group-hover:opacity-0">
                DRAG TO EXPLORE · SCROLL TO ZOOM
            </div>

            <div className="absolute bottom-4 right-4 z-10 flex flex-col gap-2">
                <button
                    data-testid={`${testIdPrefix}-zoom-in`}
                    onClick={() => zoom(-1)}
                    aria-label="Zoom in"
                    className="rounded-full border border-[#00C6FF]/30 bg-[#050B14]/70 p-2.5 text-[#00C6FF] backdrop-blur-md transition-colors hover:bg-[#00C6FF] hover:text-[#050B14]"
                >
                    <ZoomIn size={16} />
                </button>
                <button
                    data-testid={`${testIdPrefix}-zoom-out`}
                    onClick={() => zoom(1)}
                    aria-label="Zoom out"
                    className="rounded-full border border-[#00C6FF]/30 bg-[#050B14]/70 p-2.5 text-[#00C6FF] backdrop-blur-md transition-colors hover:bg-[#00C6FF] hover:text-[#050B14]"
                >
                    <ZoomOut size={16} />
                </button>
                <button
                    data-testid={`${testIdPrefix}-autorotate-toggle`}
                    onClick={toggleRotate}
                    aria-label="Toggle auto rotate"
                    className="rounded-full border border-[#00C6FF]/30 bg-[#050B14]/70 p-2.5 text-[#00C6FF] backdrop-blur-md transition-colors hover:bg-[#00C6FF] hover:text-[#050B14]"
                >
                    {rotating ? <Pause size={16} /> : <RotateCw size={16} />}
                </button>
                <button
                    data-testid={`${testIdPrefix}-fullscreen`}
                    onClick={fullscreen}
                    aria-label="Fullscreen"
                    className="rounded-full border border-[#00C6FF]/30 bg-[#050B14]/70 p-2.5 text-[#00C6FF] backdrop-blur-md transition-colors hover:bg-[#00C6FF] hover:text-[#050B14]"
                >
                    <Maximize size={16} />
                </button>
            </div>
        </div>
    );
}
